import { createUUID, resolveLocalUriAsBlob } from '../../../../backbone-unorganized/utils/files';
import { CordovaPlugins, getZencamLocalizedStrings } from '../../../../shared/CordovaPlugins';
import { base64UrlToBlob, resizeImage } from '../../../../shared/utils/files';
import { isNative, isIos, isWeb } from '../../../../shared/utils/is';
import { confirmNotification, showPermissionDeniedMessage } from '../../../../shared/utils/notifications';
import { ZenputFiles } from '../../../shared/models/files';
import { ReactPageWrapper } from '../../../shared/views/ReactPageWrapper';
import { ImageAndVideoFieldViewBase } from '../baseMediaFields/views';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { logMobileUsageMetrics } from '../../../../shared/utils/mobileUsageLogging';

import { Gallery, ImageEditor } from '@zenput/feature-zencam';
import { analyzeImageQuality } from './imageQualityChecker';

const MAX_SIZE = 2048;

export const ImageAndVideoFieldThumbnailItem = Backbone.Marionette.ItemView.extend({
    template: '#tpl_image_and_video_field_thumbnail_item',
    model: null, // ZenputFile
    collection: null, // ZenputFiles

    form_field: null, // MobileBaseField
    allow_delete: function () {
        return false;
    },
    is_draft: false,

    events: {
        'click .thumbnail': 'open_image_preview',
        'click .df-delete': 'removeImage'
    },

    templateHelpers: function () {
        if ($f.flags.get_flag('detect_low_quality_photos')) {
            return {
                thumbnail_url: this.model.get_display_url(true, this.is_draft),
                is_low_quality: this.model.get('is_low_quality')
            };
        }

        return {
            thumbnail_url: this.model.get_display_url(true, this.is_draft),
            is_low_quality: false
        };
    },

    initialize: function (options) {
        options || (options = {});
        $.extend(this, options);
    },

    removeImage: function (event) {
        const isIosDevice = isIos();
        const primaryButton = isIosDevice ? 'mobile_action_sheet_cancel' : 'mobile_drafts_button_delete';
        const secondaryButton = isIosDevice ? 'mobile_drafts_button_delete' : 'mobile_action_sheet_cancel';
        confirmNotification(
            _t('mobile_message_image_confirm_remove_image'),
            (removeAccepted) => {
                const metricName = this.model.get('is_low_quality')
                    ? 'lowQualityImageRemoved'
                    : 'goodQualityImageRemoved';
                const shouldRemove = isIosDevice ? !removeAccepted : removeAccepted; // on iOS the buttons are inverted

                if (shouldRemove) {
                    if (isWeb()) {
                        this.trigger('on_images_updated', { deleted: new ZenputFiles([this.model]) });
                    } else {
                        this.trigger('on_images_updated', {
                            deleted: [this.model.get_display_url()]
                        });
                    }

                    logMobileUsageMetrics(metricName);

                    return;
                }
            },
            _t('mobile_drafts_button_delete'),
            _t(primaryButton),
            _t(secondaryButton),
            { color: 'error' }
        );

        if (event.stopPropagation) {
            event.stopPropagation();
        }
    },

    open_image_preview: async function (event) {
        var this_ = this;

        if (isWeb()) {
            const galleryItemsPromises = this.collection.map(async (file) => {
                const displayUrl = file.get_display_url();

                if (displayUrl.startsWith('http')) {
                    return { id: file.id, src: displayUrl };
                }

                const blob = isNative()
                    ? await resolveLocalUriAsBlob(displayUrl, file.get('mimetype'))
                    : await base64UrlToBlob(file.get('url_base64'));

                return {
                    id: file.id,
                    blob: blob
                };
            });

            const galleryItems = await Promise.all(galleryItemsPromises);

            $f.mobile.nav.show_modal(ReactPageWrapper, {
                renderFunction: function () {
                    return (
                        <Gallery
                            initialItems={galleryItems}
                            startIndex={this_.collection.indexOf(this_.model)}
                            totalCount={this_.collection.length}
                            allowChanges={this_.allow_delete()}
                            onDelete={(item) => {
                                let fileModel;
                                // Files in S3 doesn't have an id in the backbone model
                                if (item.id) {
                                    fileModel = this_.collection.get(item.id);
                                } else {
                                    fileModel = this_.collection.find(function (f) {
                                        return f.get_display_url() === item.src;
                                    });
                                }
                                this_.trigger('on_images_updated', { deleted: new ZenputFiles([fileModel]) });
                            }}
                            onEdit={async (item, editedBlob) => {
                                const fileModel = this_.collection.get(item.id);
                                const newRawFile = new File([editedBlob], fileModel.get('raw_file').name, {
                                    type: editedBlob.type
                                });
                                fileModel.set('raw_file', newRawFile, { silent: true });
                                this_.trigger('on_images_updated', { modified: new ZenputFiles([fileModel]) });
                            }}
                            onGoBack={() => {
                                this.close(); // `this` is `ReactPageWrapper` here
                            }}
                            translatedStrings={{
                                loadError: _t('mobile_dialog_image_preview_message'),
                                deleteDialog: {
                                    title: _t('mobile_drafts_button_delete'),
                                    description: _t('mobile_message_image_confirm_remove_image'),
                                    confirmLabel: _t('mobile_drafts_button_delete'),
                                    cancelLabel: _t('mobile_action_sheet_cancel')
                                }
                            }}
                        />
                    );
                }
            });
            return;
        }

        var file_paths = this.collection.map(function (file) {
            return file.get_display_url();
        });

        CordovaPlugins().zencam.getZencam(
            function (urls = {}) {
                urls.deleted = urls.deleted || [];
                urls.modified = urls.modified || [];

                // convert urls returned from Zencam to exclude the s3_base, like everywhere in JS
                var to_delete = _.map(urls.deleted, function (full_url) {
                    return full_url.replace($f.storage.get_storage_base_url(), '');
                });

                this_.trigger('on_images_updated', {
                    modified: urls.modified,
                    deleted: to_delete
                });
            },
            function (error) {
                $f.mobile.logging.log_error('Error while getting zencam plugin', error);
            },
            {
                destinationType: CordovaPlugins().zencam.DestinationType.FILE_URI,
                encodingType: CordovaPlugins().zencam.EncodingType.JPEG,
                inputType: CordovaPlugins().zencam.InputType.FILE_ARRAY,
                inputData: file_paths,
                selectedImageIndex: this.collection.indexOf(this.model),
                upperBound: 1000,
                allowDelete: this.allow_delete(),
                strings: getZencamLocalizedStrings(),
                compressionPercent: 90 // @author:david This should be 100 because we don't want to re-compress,
                // but some Android devices will lock up to an infinite loop if compression
                // is 100. Setting to 90 seems to work fine.
            }
        );

        return false;
    }
});

export const ImageFieldViewThumbnails = Backbone.Marionette.CollectionView.extend({
    template: '#tpl_image_field_thumbnails',

    itemView: ImageAndVideoFieldThumbnailItem,
    itemViewEventPrefix: 'thumbnail',

    collection: null, // ZenputFiles
    form_field: null, // MobileBaseField

    allow_delete: function () {
        return false;
    },

    itemViewOptions: function () {
        return {
            collection: this.collection,
            allow_delete: this.allow_delete,
            form_field: this.form_field,
            is_draft: this.is_draft
        };
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    }
});

export const ImageFieldView = ImageAndVideoFieldViewBase.extend({
    TARGET_MEDIA_WIDTH: 1200, // do not save images with a width bigger than this
    TARGET_MEDIA_HEIGHT: 1200, // do not save images with a hight bigger than this
    TARGET_MEDIA_COMPRESSION_PCT: 90, // compress image before save to this percentage

    regions: {
        r_thumbnails: '.thumbnails'
    },

    initialize: function (options) {
        ImageFieldView.__super__.initialize.apply(this, arguments);
        this.listenTo(this.model, 'change:file change:value', this.render);
    },

    templateHelpers: function () {
        const super_helpers = ImageAndVideoFieldViewBase.__super__.templateHelpers.apply(this, arguments);
        if ($f.flags.get_flag('detect_low_quality_photos')) {
            const thumbnail_collection = this.model.get_local_and_remote_files();
            const hasLowQUalityImages = this.model.hasLowQualityImages(thumbnail_collection);

            return $.extend(super_helpers, {
                hide_caption: this.hide_caption,
                hasLowQUalityImages: hasLowQUalityImages
            });
        }

        return $.extend(super_helpers, {
            hide_caption: this.hide_caption,
            hasLowQUalityImages: false
        });
    },

    onRender: function () {
        var this_ = this;
        var is_draft =
            this.model.collection && this.model.collection.is_draft ? this.model.collection.is_draft() : false;

        // Make sure we have auth in case we're showing already uploaded images.
        // In most cases, we'll already have it and this will take no additional time.
        $f.storage.refresh({
            complete: function () {
                if (!this_.r_thumbnails) {
                    return;
                }
                this_.r_thumbnails.show(
                    new ImageFieldViewThumbnails({
                        form_field: this_.model,
                        collection: this_.model.get_local_and_remote_files(),
                        allow_delete: this_.allow_delete,
                        is_draft: is_draft
                    })
                );

                this_.listenTo(
                    this_.r_thumbnails.currentView,
                    'thumbnail:on_images_updated',
                    function (view, zencam_changes) {
                        var to_delete = null;

                        if (isWeb()) {
                            this_.model.save_files({
                                delete: zencam_changes.deleted,
                                modify: zencam_changes.modified
                            });
                            return;
                        } else {
                            to_delete = this_._to_file_collection(zencam_changes.deleted);
                        }

                        this_.model.save_files({
                            modify: this_._to_file_collection(zencam_changes.modified),
                            delete: to_delete
                        });
                    }
                );
            }
        });
    },

    // @author:david this will only work after the surrounding div's have been drawn, so calling this at initialize
    // will not work as expected.
    allow_delete: function () {
        return $('.historical_submission').length === 0;
    },

    collect_media_camera: async function () {
        try {
            const photo = await Camera.getPhoto({
                quality: 90,
                resultType: CameraResultType.Uri,
                width: MAX_SIZE,
                height: MAX_SIZE,
                source: CameraSource.Camera
            });

            const photoUrl = photo.path;

            if ($f.flags.get_flag('detect_low_quality_photos')) {
                const imageAnalysisResult = await analyzeImageQuality(photo.webPath);

                const imageQualityStats = {
                    laplacian_variance: imageAnalysisResult.laplacianVariance,
                    is_low_quality: imageAnalysisResult.isBad
                };

                if (imageAnalysisResult.isBad) {
                    confirmNotification(
                        _t('mobile_photo_quality_dialog_message'),
                        (retakePhoto) => {
                            if (retakePhoto) {
                                logMobileUsageMetrics('lowQualityPhotoRetake');
                                this.collect_media_camera();
                                return;
                            } else {
                                logMobileUsageMetrics('lowQualityPhotoWarningIgnored');
                                this.on_collect_media_success(this._to_file_collection([photoUrl]), imageQualityStats);
                            }
                        },
                        _t('mobile_photo_quality_warning_header'),
                        _t('mobile_take_new_photo'),
                        _t('mobile_use_this_photo')
                    );
                } else {
                    this.on_collect_media_success(this._to_file_collection([photoUrl]), imageQualityStats);
                }
            } else {
                this.on_collect_media_success(this._to_file_collection([photoUrl]));
            }
        } catch (error) {
            if (error.message === 'User cancelled photos app') {
                return;
            }

            this.on_collect_media_error(error, 'camera');
        }
    },

    collect_media_gallery: function () {
        var this_ = this;

        this.required_permissions = 'photos';
        this.is_gallery_permission_granted(async function (granted) {
            if (!granted) {
                showPermissionDeniedMessage(
                    _t('mobile_message_media_perimission_denied') + _t('mobile_message_media_perimission_denied_device')
                );
                return;
            }

            try {
                const files = await Camera.pickImages({
                    quality: 90,
                    width: MAX_SIZE,
                    height: MAX_SIZE
                });

                if (!files || !files.photos.length) {
                    return; // user cancelled
                }

                if ($f.flags.get_flag('detect_low_quality_photos')) {
                    for (const photoFile of files.photos) {
                        const imageQualityAnalysisResult = await analyzeImageQuality(photoFile.webPath);

                        const imageQualityStats = {
                            laplacian_variance: imageQualityAnalysisResult.laplacianVariance,
                            is_low_quality: imageQualityAnalysisResult.isBad
                        };

                        this_.on_collect_media_success(this_._to_file_collection([photoFile.path]), imageQualityStats);
                    }
                } else {
                    this_.on_collect_media_success(this_._to_file_collection(files.photos.map((f) => f.path)));
                }
            } catch (error) {
                if (error.message === 'No images picked' || error.message === 'User cancelled photos app') {
                    return;
                }
                this_.on_collect_media_error(error, 'library');
            }
        });
    },

    collect_media_web: function () {
        var this_ = this;

        $f.mobile.uploads.web_filestore.pick_new_file(
            {
                allowed_file_types: 'image/*',
                allow_multiple: true,
                is_media: true
            },
            async function (files_list) {
                await Promise.all(
                    files_list.map(async (file) => {
                        const rawFile = file.get('raw_file');
                        const extension = rawFile.name.match(/(\.\w+$)/)?.[0];
                        const renamedFile = new File([rawFile], `${createUUID()}${extension || '.jpg'}`, {
                            type: rawFile.type
                        });
                        // It also changes to jpeg
                        const resizedFile = await resizeImage(renamedFile, MAX_SIZE);

                        if ($f.flags.get_flag('detect_low_quality_photos')) {
                            const filePath = URL.createObjectURL(rawFile);

                            const imageQualityAnalysisResult = await analyzeImageQuality(filePath);
                            file.set('laplacian_variance', imageQualityAnalysisResult.laplacianVariance);
                            file.set('is_low_quality', imageQualityAnalysisResult.isBad);

                            URL.revokeObjectURL(filePath);
                        }

                        file.set('raw_file', resizedFile);
                    })
                );

                this_.on_collect_media_success(files_list);
            }
        );
    },

    openImageEditor: function (image) {
        const _this = this;
        $f.mobile.nav.show_modal(ReactPageWrapper, {
            renderFunction: function () {
                return (
                    <ImageEditor
                        image={image}
                        onGoBack={() => {
                            _this.collectMediaWebCamera();
                        }}
                        onSave={async (blob) => {
                            if ($f.flags.get_flag('detect_low_quality_photos')) {
                                const filePath = URL.createObjectURL(blob);

                                const imageAnalysisResult = await analyzeImageQuality(filePath);

                                const imageQualityStats = {
                                    laplacian_variance: imageAnalysisResult.laplacianVariance,
                                    is_low_quality: imageAnalysisResult.isBad
                                };

                                URL.revokeObjectURL(filePath);

                                if (imageAnalysisResult.isBad) {
                                    confirmNotification(
                                        _t('mobile_photo_quality_dialog_message'),
                                        (retakePhoto) => {
                                            if (retakePhoto) {
                                                logMobileUsageMetrics('lowQualityPhotoRetake');
                                                _this.collectMediaWebCamera();
                                                return;
                                            } else {
                                                logMobileUsageMetrics('lowQualityPhotoWarningIgnored');
                                                _this.addMediaFileToStorage(blob, imageQualityStats);
                                                this.close();
                                            }
                                        },
                                        _t('mobile_photo_quality_warning_header'),
                                        _t('mobile_take_new_photo'),
                                        _t('mobile_use_this_photo')
                                    );
                                } else {
                                    _this.addMediaFileToStorage(blob, imageQualityStats);
                                    this.close();
                                }
                            } else {
                                _this.addMediaFileToStorage(blob);
                                this.close();
                            }
                        }}
                        translatedStrings={{
                            exportErrorMessage: _t('mobile_field_image_picker_edit_failed')
                        }}
                    />
                );
            }
        });
    }
});
